import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { FileText } from "lucide-react";
import { getProposalStatus } from "@/constants/proposalMeta";
import { calculateWeightedValue } from "@/utils/proposalUtils";

const ProposalsChartSkeleton = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Skeleton className="h-5 w-5" />
        <Skeleton className="h-6 w-48" />
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-80 flex items-center justify-center">
        <Skeleton className="h-full w-full" />
      </div>
    </CardContent>
  </Card>
);

export function ProposalsChart() {
  console.log('ProposalsChart: Component rendering...');
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const yearOptions = [2025, 2026, 2027];
  
  const { data: queryResult, isLoading, error } = useQuery({
    queryKey: ["proposals-chart-fixed-v4", selectedYear],
    queryFn: async () => {
      console.log('ProposalsChart: Starting query for year:', selectedYear);
      
      try {
        const { data, error } = await supabase
          .from("proposals")
          .select(`
            sent_date, 
            status, 
            title,
            value,
            final_value,
            probability,
            response_deadline,
            client_id,
            clients(name)
          `)
          .not('sent_date', 'is', null)
          .gte('sent_date', `${selectedYear}-01-01`)
          .lte('sent_date', `${selectedYear}-12-31`)
          .order("sent_date", { ascending: true });

        console.log('ProposalsChart: Raw query result:', { 
          error, 
          dataLength: data?.length, 
          firstProposal: data?.[0] 
        });

        if (error) {
          console.error('ProposalsChart: Query error:', error);
          throw error;
        }
        
        if (!data || data.length === 0) {
          console.log('ProposalsChart: No data found - returning empty result');
          return { chartData: [], summaryData: { pending: { count: 0, value: 0 }, accepted: { count: 0, value: 0 }, rejected: { count: 0, value: 0 } } };
        }

        console.log('ProposalsChart: Processing', data.length, 'proposals...');
        console.log('ProposalsChart: Sample proposal:', data[0]);
        
        // Calculate summary data first
        const summaryData = {
          pending: { count: 0, value: 0 },
          accepted: { count: 0, value: 0 },
          rejected: { count: 0, value: 0 }
        };

        // Group by month with detailed proposal info
        const monthlyData: { [key: string]: { 
          pending: Array<{clientName: string, title: string, value: number, weightedValue: number}>, 
          accepted: Array<{clientName: string, title: string, value: number, weightedValue: number}>, 
          rejected: Array<{clientName: string, title: string, value: number, weightedValue: number}> 
        } } = {};
        
        data.forEach(proposal => {
          if (proposal.sent_date) {
            const date = new Date(proposal.sent_date);
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!monthlyData[monthYear]) {
              monthlyData[monthYear] = { 
                pending: [], 
                accepted: [], 
                rejected: [] 
              };
            }
            
            const actualStatus = getProposalStatus(proposal);
            const clientName = (proposal as any).clients?.name || 'Unknown Client';
            const proposalValue = Number(proposal.final_value || proposal.value || 0);
            const weightedValue = calculateWeightedValue(proposalValue, proposal.probability);
            
            const proposalData = { 
              clientName, 
              title: proposal.title,
              value: proposalValue, 
              weightedValue 
            };
            
            if (actualStatus === 'overdue') {
              console.log('ProposalsChart: Overdue proposal:', { 
                title: proposal.title, 
                sent_date: proposal.sent_date, 
                response_deadline: proposal.response_deadline,
                actualStatus 
              });
            }
            
            if (actualStatus === 'accepted') {
              monthlyData[monthYear].accepted.push(proposalData);
              summaryData.accepted.count++;
              summaryData.accepted.value += proposalValue;
            } else if (actualStatus === 'rejected') {
              monthlyData[monthYear].rejected.push(proposalData);
              summaryData.rejected.count++;
              summaryData.rejected.value += proposalValue;
            } else {
              // This includes 'pending', 'overdue', and any other status
              monthlyData[monthYear].pending.push(proposalData);
              summaryData.pending.count++;
              summaryData.pending.value += proposalValue;
            }
          }
        });

        // Generate all 12 months for the selected year
        const allMonths: string[] = [];
        for (let month = 1; month <= 12; month++) {
          allMonths.push(`${selectedYear}-${String(month).padStart(2, '0')}`);
        }
        
        const result = allMonths.map(monthYear => {
          const [year, month] = monthYear.split('-');
          const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('el-GR', { 
            month: 'short',
            year: '2-digit'
          });
          
          const data = monthlyData[monthYear] || { 
            pending: [], 
            accepted: [], 
            rejected: [] 
          };
          
          const result = {
            month: monthName,
            pending: data.pending.length,
            accepted: data.accepted.length,
            rejected: data.rejected.length,
            total: data.pending.length + data.accepted.length + data.rejected.length,
            fullDate: monthYear,
            proposalsData: data
          };
          
          if (data.pending.length > 0 || data.accepted.length > 0 || data.rejected.length > 0) {
            console.log(`ProposalsChart: Month ${monthYear} (${monthName}) data:`, {
              pending: data.pending.length,
              accepted: data.accepted.length, 
              rejected: data.rejected.length,
              total: result.total
            });
          }
          return result;
        });

        console.log('ProposalsChart: Final summary totals:', {
          pending: summaryData.pending.count,
          accepted: summaryData.accepted.count,
          rejected: summaryData.rejected.count,
          chartPendingSum: result.reduce((sum, month) => sum + month.pending, 0),
          chartAcceptedSum: result.reduce((sum, month) => sum + month.accepted, 0),
          chartRejectedSum: result.reduce((sum, month) => sum + month.rejected, 0)
        });

        return { chartData: result, summaryData };
        
      } catch (err) {
        console.error('ProposalsChart: Exception in query:', err);
        throw err;
      }
    },
    staleTime: 0, // Force refresh
  });

  const chartData = queryResult?.chartData || [];
  const summaryData = queryResult?.summaryData || { pending: { count: 0, value: 0 }, accepted: { count: 0, value: 0 }, rejected: { count: 0, value: 0 } };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  console.log('ProposalsChart: Render state:', { isLoading, error: error?.message, chartDataLength: chartData.length });

  if (isLoading) {
    return <ProposalsChartSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Proposals per Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            Failed to load proposals chart data: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Proposals per Month
          </CardTitle>
          <Select
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-3 gap-4 pt-4">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-warning">
              {summaryData.pending.count}
            </div>
            <div className="text-sm text-muted-foreground">Pending</div>
            <div className="text-sm font-medium text-foreground">
              {formatCurrency(summaryData.pending.value)}
            </div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-success">
              {summaryData.accepted.count}
            </div>
            <div className="text-sm text-muted-foreground">Accepted</div>
            <div className="text-sm font-medium text-foreground">
              {formatCurrency(summaryData.accepted.value)}
            </div>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-destructive">
              {summaryData.rejected.count}
            </div>
            <div className="text-sm text-muted-foreground">Rejected</div>
            <div className="text-sm font-medium text-foreground">
              {formatCurrency(summaryData.rejected.value)}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const tooltipData = payload[0]?.payload;
                    const proposalsData = tooltipData?.proposalsData;
                    console.log('Tooltip proposalsData:', proposalsData);
                    console.log('Tooltip tooltipData:', tooltipData);
                    
                    const statusColors = {
                      pending: 'hsl(var(--warning))',
                      accepted: 'hsl(var(--success))',
                      rejected: 'hsl(var(--destructive))'
                    };
                    
                    // Convert Greek month to English
                    const englishMonth = tooltipData?.fullDate ? 
                      new Date(tooltipData.fullDate + '-01').toLocaleDateString('en-US', { 
                        month: 'short'
                      }) : label;
                    
                    // Collect all proposals grouped by status
                    const allProposals = [
                      ...proposalsData?.pending?.map((p: any) => ({ ...p, status: 'pending' })) || [],
                      ...proposalsData?.accepted?.map((p: any) => ({ ...p, status: 'accepted' })) || [],
                      ...proposalsData?.rejected?.map((p: any) => ({ ...p, status: 'rejected' })) || []
                    ];
                    
                    return (
                      <div 
                        className="rounded-lg border bg-background p-2 shadow-lg" 
                        style={{ width: 'fit-content', minWidth: '280px', maxWidth: '320px', maxHeight: '300px', overflowY: 'auto' }}
                      >
                        <div className="mb-2">
                          <div className="text-xs font-bold text-foreground">
                            {englishMonth} • {allProposals.length} proposal{allProposals.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        
                        {allProposals.length > 0 ? (
                          <div className="space-y-1">
                            {allProposals.map((proposal: any, index: number) => (
                              <div key={index} className="flex items-center gap-2 py-0.5">
                                <div 
                                  className="w-2 h-2 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: statusColors[proposal.status as keyof typeof statusColors] }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-foreground truncate" title={proposal.clientName}>
                                    {proposal.clientName}
                                  </div>
                                </div>
                                <div className="text-xs font-bold flex-shrink-0" style={{ color: statusColors[proposal.status as keyof typeof statusColors] }}>
                                  {formatCurrency(proposal.weightedValue)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">
                            No proposals this month
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              
              <Bar 
                dataKey="pending" 
                stackId="proposals"
                fill="hsl(var(--warning))" 
                radius={[0, 0, 0, 0]}
              >
                <LabelList 
                  dataKey="pending" 
                  position="center"
                  content={(props: any) => {
                    const { x, y, width, height, value } = props;
                    if (!value || value === 0) return null;
                    const centerX = Number(x) + Number(width) / 2;
                    const centerY = Number(y) + Number(height) / 2;
                    return (
                      <text
                        x={centerX}
                        y={centerY}
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="11"
                        fontWeight="600"
                      >
                        {value}
                      </text>
                    );
                  }}
                />
              </Bar>
              
              <Bar 
                dataKey="accepted" 
                stackId="proposals"
                fill="hsl(var(--success))" 
                radius={[0, 0, 0, 0]}
              >
                <LabelList 
                  dataKey="accepted" 
                  position="center"
                  content={(props: any) => {
                    const { x, y, width, height, value } = props;
                    if (!value || value === 0) return null;
                    const centerX = Number(x) + Number(width) / 2;
                    const centerY = Number(y) + Number(height) / 2;
                    return (
                      <text
                        x={centerX}
                        y={centerY}
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="11"
                        fontWeight="600"
                      >
                        {value}
                      </text>
                    );
                  }}
                />
              </Bar>
              
              <Bar 
                dataKey="rejected" 
                stackId="proposals"
                fill="hsl(var(--destructive))" 
                radius={[4, 4, 0, 0]}
              >
                <LabelList 
                  dataKey="rejected" 
                  position="center"
                  content={(props: any) => {
                    const { x, y, width, height, value } = props;
                    if (!value || value === 0) return null;
                    const centerX = Number(x) + Number(width) / 2;
                    const centerY = Number(y) + Number(height) / 2;
                    return (
                      <text
                        x={centerX}
                        y={centerY}
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="11"
                        fontWeight="600"
                      >
                        {value}
                      </text>
                    );
                  }}
                />
                <LabelList 
                  dataKey="total" 
                  position="top"
                  content={(props: any) => {
                    const { x, y, width, index } = props;
                    if (!chartData || !chartData[index] || !chartData[index].total || chartData[index].total === 0) return null;
                    const centerX = Number(x) + Number(width) / 2;
                    const topY = Number(y) - 8;
                    return (
                      <text
                        x={centerX}
                        y={topY}
                        fill="hsl(var(--foreground))"
                        textAnchor="middle"
                        dominantBaseline="bottom"
                        fontSize="12"
                        fontWeight="bold"
                      >
                        {chartData[index].total}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}